import { Component, Input, Output, EventEmitter, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { translate, TranslocoPipe } from '@jsverse/transloco';
import { WhoAmI } from '@libs/vc-core-lib';
import { IOrgModel } from '@libs/vc-org-lib';
import { TagsComponent } from '../tags-selector';
import { isEmpty } from 'lodash-es';
import { BoldBySearch } from '../pipes/bold-by-search/bold-by-search.pipe';
import {
    DialogComponent,
    IconComponent,
    InputComponent,
    ProgressSpinnerDirective,
    TreeComponent,
    TreeNode,
} from 'vcp-common-ui-lib';
import { MatTooltip } from '@angular/material/tooltip';

interface OrgNode {
    term: string;
    name: string;
    id: string;
    tags: string[];
}

@Component({
    selector: 'vc-org-picker',
    templateUrl: 'org-picker.component.html',
    styleUrls: ['org-picker.component.scss'],
    imports: [
        TranslocoPipe,
        TagsComponent,
        BoldBySearch,
        DialogComponent,
        InputComponent,
        TreeComponent,
        ProgressSpinnerDirective,
        IconComponent,
        MatTooltip,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgPickerComponent {
    private _previousHighlighted!: TreeNode<OrgNode> | null;
    readonly filterLabel: string = translate('COMMON_UI.TREE.FILTER');
    readonly filterPlaceholder: string = translate('COMMON_UI.TREE.FILTER');

    orgTreeNodes: TreeNode<OrgNode>[] = [];
    selectedOrgNode!: TreeNode<OrgNode> | null;
    filterValue!: string;

    @Input()
    loading: boolean = false;

    @Input()
    set selectedOrg(org: IOrgModel | null) {
        this._selectedOrg = org;
        if (!isEmpty(this.orgTreeNodes)) {
            this._setHighlightedNode(this.orgTreeNodes);
        }
    }
    get selectedOrg(): IOrgModel | null {
        return this._selectedOrg;
    }
    private _selectedOrg!: IOrgModel | null;

    @Input()
    user!: WhoAmI;

    @Input()
    set visible(value: boolean) {
        if (this._visible !== value) {
            this._visible = value;
            if (!this._visible) {
                this._refreshSelection();
            }
            this.visibleChange.emit(this._visible);
        }
    }

    get visible(): boolean {
        return this._visible;
    }

    private _visible: boolean = false;

    @Input()
    set orgModels(values: IOrgModel[]) {
        this._orgModels = values;
        if (values) {
            this._initOrgTree();
        }
    }

    get orgModels(): IOrgModel[] {
        return this._orgModels;
    }

    private _orgModels: IOrgModel[] = [];

    @Input()
    title: string = translate('CORE.USER.CHANGE_ORGANIZATION');

    @Input()
    primaryLabel: string = translate('COMMON_UI.SAVE');

    @Input()
    secondaryLabel: string = translate('COMMON_UI.CANCEL');

    @Input()
    showTags: boolean = false;

    /** Outputs filter value to allow BE search; Will search existing list by default **/
    @Input()
    searchByFilter: boolean = false;

    @Output()
    filterValueChange = new EventEmitter<string>();

    @Output()
    visibleChange = new EventEmitter<boolean>();

    @Output()
    selectedOrgChange = new EventEmitter<IOrgModel>();

    @ViewChild('orgTree', { static: false })
    orgTreeComponent!: TreeComponent<IOrgModel>;

    selectOrg() {
        if (this.selectedOrgNode?.data?.id) {
            const selectedOrg = this.orgModels.find((org: IOrgModel) => org.id === this.selectedOrgNode?.data.id);
            if (selectedOrg) {
                this.selectedOrgChange.emit(selectedOrg);
            }
        }
    }

    orgItemRenderer = (value: OrgNode) => value?.name ?? '';

    private _convertToTreeView(data: TreeNode<OrgNode>[], list: IOrgModel[]) {
        list.forEach((record: IOrgModel) => {
            const paths = record.paths.filter((pdata: string) => pdata.startsWith('/org/'));
            const path: string = paths[0].trim().substring(5);
            const pathTerms: Array<string> = path.split('/');
            let currentData = data;

            pathTerms.forEach((pathTerm: string) => {
                let found: boolean = false;
                currentData.forEach((d: TreeNode<OrgNode>) => {
                    if (d?.data?.term == pathTerm) {
                        currentData = d.children ?? [];
                        found = true;
                    }
                });

                if (!found) {
                    const org: IOrgModel | undefined = list.find((org: IOrgModel) => org.id === pathTerm);
                    if (org) {
                        const newData: TreeNode<OrgNode> = {
                            data: {
                                term: pathTerm,
                                name: org.name,
                                id: org.id,
                                tags: org.tags,
                            },
                            children: [],
                            selectable: true,
                            iconPrefix: 'business',
                        };
                        currentData.push(newData);
                        currentData = newData.children ?? [];
                    }
                }
            });
        });
    }

    private _refreshSelection() {
        if (this._previousHighlighted && this.selectedOrgNode?.data.id != this._previousHighlighted?.data.id) {
            this.selectedOrgNode = this._previousHighlighted;
        }
    }

    private _initOrgTree() {
        this.selectedOrgNode = null;
        this.orgTreeNodes = [];
        this._convertToTreeView(this.orgTreeNodes, this.orgModels);
        this._setHighlightedNode(this.orgTreeNodes);
    }

    private _setHighlightedNode(values: TreeNode<OrgNode>[]) {
        const orgId = this.user?.getOrgId();
        values.forEach((node: TreeNode<OrgNode>) => {
            if (node.data?.id === orgId) {
                this.selectedOrgNode = node;
                this._previousHighlighted = node;
            }

            if (node?.children && node?.children?.length > 0) {
                this._setHighlightedNode(node?.children);
            }

            if (!this.selectedOrg) {
                this.selectedOrgNode = null;
            }
        });
    }
}
